import './App.css';
import './font.css';

import {
  colors,
  ImpContainer,
  ImpSnackbarProvider,
} from '@imprint-payments/imprint-ui';
import { useStatsigClient } from '@statsig/react-bindings';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { EVENT } from './analytics/analyticsConsts';
import {
  getCardOfferImg,
  getCheckApplicationQR,
  getColor,
  getConfirmLaterQR,
  getDownloadAppQRCode,
  getErrorQR,
  getLogoIcon,
  getManualReviewQR,
  getRewardDetails,
  getSuccessQR,
  getVirtualCard,
} from './api/api';
import { getLockupImage } from './api/getLockupImage/getLockupImage';
import { fetchPartnerBrandingFromContentful } from './api/onboarding/contentful';
import {
  setClientSecret,
  setPartnerCustomerId,
  setSessionID,
} from './app/apiScreenSlice';
import { setLoading } from './app/applicationScreenSlice';
import { AppHistory } from './app/History';
import { useAppSelector, useImpState } from './app/hooks';
import { setLanguage } from './app/languageSlice';
import { store } from './app/store';
import { ApplicationLoader } from './components/ApplicationLoader/ApplicationLoader';
import Login from './components/sso/login';
import fetchFirebaseApiKey, {
  ANALYTICS,
  config,
  initFeatureFlags,
} from './firebase/firebase';
import { useTagManager } from './hooks/useTagManager';
import { useVendorSpecificTag } from './hooks/useVendorSpecificTag';
import { useDatadogLogs, useDatadogRum } from './observability/datadog';
import Accept from './pages/accept/AcceptApplicaton';
import AccountStatusPage from './pages/accountStatusPage/AccountStatusPage';
import CheckApplication from './pages/checkApplication/CheckApplication';
import { CompleteOnPhonePage } from './pages/CompleteOnPhonePage';
import { CreatePINPage } from './pages/CreatePINPage';
import AsyncApplicationTimeout from './pages/error/AsyncApplicationTimeout';
import Error from './pages/error/Error';
import ApplicationHome from './pages/home/ApplicationHome';
import ApplicationLandingPage from './pages/home/ApplicationLandingPage';
import OfferPage from './pages/home/OfferPage';
import OnboardingFlow from './pages/onboarding/OnboardingFlow';
import OtpVerify from './pages/otp/OtpVerify';
import Store from './pages/store';
import Gates from './statsig/gates';
import useStatsigUserUpdaters from './statsig/user';
import {
  fetchEnvFromDomain,
  getMerchantKeyFromPath,
  initProduct,
} from './utils/environments';
import {
  getLanguageFromCookie,
  setSessionUrlParam,
} from './utils/functionUtils';
import { LanguageCode } from './utils/language';
import {
  getCustomerSessionFromClientSecret,
  merchantInfoByProductUUID,
  PRODUCT_UUID,
} from './utils/productUUIDConstants';
import { PAGE } from './utils/routeConstant';
import {
  emitSDKResult,
  SDK_ERROR_CODES,
  SDK_RESULTS,
  useSDKContext,
} from './utils/sdk';
import { SESSION_STORAGE } from './utils/sessionStorageConstants';
import { useExternalScript } from './utils/useExternalScript';

const AppWrapper = () => {
  const queryClient = new QueryClient();

  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Provider>
  );
};
export default AppWrapper;

const NavigateSetter = () => {
  AppHistory.navigate = useNavigate();

  return null;
};

function App() {
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [searchParams] = useSearchParams();
  const { setResult, setErrorCode } = useSDKContext();
  const location = useLocation();

  const [, setUrlSearchParams] = React.useState(new URLSearchParams());

  const [productInitialized, setProductInitialized] = useState(false);

  const {
    language,
    languageCode,
    merchantData,
    screenState,
    featureFlags,
    apiScreenState,
    acceptScreenState,
    consumerInfo,
    dispatch,
  } = useImpState();

  useStatsigUserUpdaters();

  const { consumerUUID } = consumerInfo;
  const { productUUID } = merchantData;
  const { sessionID } = apiScreenState;
  const merchantKey = getMerchantKeyFromPath(window.location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screenState, location.pathname]);

  useDatadogRum({
    consumerUUID,
    productUUID,
    merchantKey,
    sessionID,
    productInitialized,
  });
  useDatadogLogs({
    consumerUUID,
    productUUID,
    merchantKey,
    sessionID,
    productInitialized,
  });
  useTagManager(productUUID);
  useVendorSpecificTag(productUUID);

  useEffect(() => {
    try {
      const sessionID =
        sessionStorage.getItem(SESSION_STORAGE.SESSION_ID) ??
        crypto.randomUUID();

      sessionStorage.setItem(SESSION_STORAGE.SESSION_ID, sessionID);
      dispatch(setSessionID(sessionID));
    } catch (err) {
      console.error('Unable to write session_id to sessionStorage', err);
    }
  }, [dispatch]);

  useEffect(() => {
    setUrlSearchParams(new URLSearchParams(window.location.search));

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [acceptScreenState.redirectToLogin, screenState.screen]);

  useEffect(() => {
    setSessionUrlParam(window.location.search, dispatch);
    window.onbeforeunload = function (_e) {
      ANALYTICS.analyticsUtil.writeStoredEvents();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const initialize = async () => {
      dispatch(setLoading(true));
      initFeatureFlags();
      await initProductFromUrl();
      fetchFirebaseApiKey();
      dispatch(setLoading(false));
      setProductInitialized(true);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initProductFromUrl = async () => {
    const { env, pUUID } = fetchEnvFromDomain(
      window.location.hostname.toLowerCase(),
      window.location.pathname.toLowerCase()
    );

    const clientSecret = searchParams.get('client_secret');

    if (clientSecret) {
      // This is an application coming from partner SDK: Initialize product by productUUID
      sessionStorage.setItem(SESSION_STORAGE.IS_SDK, 'true');

      const customerSession =
        await getCustomerSessionFromClientSecret(clientSecret);

      if (customerSession?.product_uuid) {
        const {
          product_uuid: productUUID,
          partner_customer_id: partnerCustomerId,
        } = customerSession;
        ANALYTICS.logEvent(EVENT.SDK_APP_INIT, {
          env,
          client_secret: clientSecret,
          product_uuid: productUUID,
          ...(partnerCustomerId && { partner_customer_id: partnerCustomerId }),
        });

        store.dispatch(setClientSecret(clientSecret));
        store.dispatch(setPartnerCustomerId(partnerCustomerId));
        initProduct(productUUID, env);
      } else {
        ANALYTICS.logEvent(EVENT.SDK_APP_INIT_ERROR, {
          env,
          client_secret: clientSecret,
          error_code: SDK_ERROR_CODES.INVALID_CLIENT_SECRET,
        });

        setResult(SDK_RESULTS.ERROR);
        setErrorCode(SDK_ERROR_CODES.INVALID_CLIENT_SECRET);
        emitSDKResult({
          result: SDK_RESULTS.ERROR,
          errorCode: SDK_ERROR_CODES.INVALID_CLIENT_SECRET,
        });
        return;
      }
    } else {
      ANALYTICS.logEvent(EVENT.DIRECT_APP_INIT, {
        env,
        product_uuid: pUUID,
      });

      const shouldBlock =
        merchantInfoByProductUUID[pUUID]?.shouldBlockExternalApply &&
        sessionStorage.getItem(SESSION_STORAGE.IS_SDK) !== 'true';
      // This is a direct application: Initialize the product directly from domain/path
      // Do not initialize if the partner blocks direct application
      initProduct(shouldBlock ? '' : pUUID, env);
    }
  };

  // get language from session
  useEffect(() => {
    dispatch(setLoading(true));
    let language = LanguageCode.English;
    language = getLanguageFromCookie();
    dispatch(setLanguage(language));
    dispatch(setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureFlags.spanish]);

  const internalAuth = useAppSelector(
    (state) => state.apiScreenScreenSelector.internalAuthRequired
  );

  useEffect(() => {
    const root = document.documentElement;
    const { light, dark, contrastLight, contrastDark } = merchantData.color;

    if (merchantData.productUUID === PRODUCT_UUID.HEB) {
      //! This is a temporary workaround for HEB so that we can stay WCAG AA compliant
      //! (HEB's red brand color causes contrast-ratio accessibility issues)
      // Imprint neutral 950, which is #232323
      root.style.setProperty('--lightColor', colors.content.onSurface);
      root.style.setProperty('--merchant-color', colors.content.onSurface);
    } else {
      root.style.setProperty('--lightColor', light);
      root.style.setProperty('--merchant-color', light);
    }

    root.style.setProperty('--merchant-color-dark', dark);
    if (contrastLight) {
      root.style.setProperty('--contrastLight', contrastLight);
    }
    if (contrastDark) {
      root.style.setProperty('--contrastDark', contrastDark);
    }
  }, [merchantData.color, merchantData.productUUID]);

  // placesLoadedCallback is required for the Google Places API (added in index.html)
  useExternalScript(
    'https://maps.googleapis.com/maps/api/js?key=' +
      config.mapKey +
      '&libraries=places&loading=async&callback=placesLoadedCallback'
  );

  const { checkGate } = useStatsigClient();
  useEffect(() => {
    if (merchantData.productUUID) {
      dispatch(getVirtualCard(merchantData.productUUID));
      dispatch(getCardOfferImg(merchantData.productUUID));
      dispatch(getLockupImage(merchantData.productUUID));
      dispatch(getRewardDetails(merchantData.productUUID, languageCode));
      dispatch(getLogoIcon(merchantData.productUUID));
      dispatch(getSuccessQR(merchantData.productUUID));
      dispatch(getErrorQR(merchantData.productUUID));
      dispatch(getCheckApplicationQR(merchantData.productUUID));
      dispatch(getManualReviewQR(merchantData.productUUID));
      dispatch(getConfirmLaterQR(merchantData.productUUID));
      dispatch(getDownloadAppQRCode(merchantData.productUUID));
      if (checkGate(Gates.Contentful)) {
        dispatch(fetchPartnerBrandingFromContentful(merchantData.productUUID));
      } else {
        dispatch(getColor(merchantData.productUUID));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantData.productUUID, language]);

  useEffect(() => {
    const assetsToLoad = [
      merchantData.cardImg,
      merchantData.logoIcon,
      merchantData.color.light,
      merchantData.rewardDetails.rewards,
      merchantData.rewardDetails.rewardsBonus,
      featureFlags,
    ];

    let areLoaded = true;
    assetsToLoad.forEach((asset) => {
      if (!asset && asset === '') {
        areLoaded = false;
      }
    });

    setAssetsLoaded(areLoaded);
  }, [
    merchantData.cardImg,
    merchantData.logoIcon,
    merchantData.color.light,
    merchantData.rewardDetails.rewards,
    merchantData.rewardDetails.rewardsBonus,
    featureFlags,
  ]);

  return (
    <main className="App">
      <ImpSnackbarProvider>
        <NavigateSetter />
        <ImpContainer>
          {internalAuth ? (
            <Login />
          ) : (
            <Routes>
              <Route path={PAGE.START} element={<ApplicationLandingPage />} />
              <Route path={PAGE.ERROR} element={<Error />} />
              {assetsLoaded ? (
                <>
                  <Route path="*" element={<Navigate to={PAGE.HOME} />} />
                  {checkGate(Gates.InStoreApplicationV2) && (
                    <Route path={PAGE.STORE} element={<Store />} />
                  )}
                  <Route path={PAGE.HOME} element={<ApplicationHome />} />
                  <Route path={PAGE.CREDIT} element={<ApplicationHome />} />
                  <Route
                    path={PAGE.CREDIT_PROGRAM}
                    element={<ApplicationHome />}
                  />
                  <Route path={PAGE.OTP} element={<OtpVerify />} />
                  <Route
                    path={PAGE.ASYNC_APP_TIMEOUT}
                    element={<AsyncApplicationTimeout />}
                  />
                  <Route
                    path={PAGE.CHECK_APPLICATION}
                    element={<CheckApplication />}
                  />
                  <Route path={PAGE.OFFER} element={<OfferPage />} />
                  <Route path={PAGE.ACCEPT} element={<Accept />} />
                  <Route
                    path={PAGE.ACCOUNT_STATUS}
                    element={<AccountStatusPage isFullScreen />}
                  />
                  <Route path={PAGE.CREATE_PIN} element={<CreatePINPage />} />
                  <Route
                    path={PAGE.COMPLETE_ON_PHONE}
                    element={<CompleteOnPhonePage />}
                  />
                  <Route path={PAGE.ONBOARDING} element={<OnboardingFlow />} />
                </>
              ) : (
                <Route path="*" element={<ApplicationLoader />} />
              )}
            </Routes>
          )}
        </ImpContainer>
      </ImpSnackbarProvider>
    </main>
  );
}
